import React, { useMemo } from "react";

import "./resource_plan_dashboard.less";
import { COLORS } from "@src/constants";
import { RequestStatusStatisticsChart } from "@src/features/request_status_statistics_chart/components/request_status_statistics_chart";
import {
  calculateResourcePlanDashboardData,
  getResourcePlanChartSettings,
} from "@src/features/resource_plan/utils/resource_plan_dashboard_utils";
import { TotalFteChart } from "@src/features/total_fte_chart/components/total_fte_chart";
import { useGetRoleRequestsByProjectIdQuery } from "@src/services/apis/projectsApi";
import { RootState, useAppSelector } from "@src/setupStore";
import {
  MonthlyTotalFte,
  ResourcePlanRequestStatus,
} from "@src/types/dashboard_types";
import { Col, Row } from "antd";

import { RequestTotalCard } from "../request_total_card";

interface ResourcePlanDashboardProps {
  projectId: number;
}

interface ResourcePlanDashboard {
  requestStatusTotals: ResourcePlanRequestStatus;
  totalFTE: MonthlyTotalFte[];
}

const ResourcePlanDashboard: React.FC<ResourcePlanDashboardProps> = ({
  projectId,
}) => {
  const {
    isFetching,
    isUninitialized,
    data: roleRequestData,
  } = useGetRoleRequestsByProjectIdQuery(projectId?.toString(), {
    skip: !projectId,
  });

  const {
    isProjectEnded,
    resourcePlanTableState: { isTableDisabled },
  } = useAppSelector((state: RootState) => state.resourcePlanSlice);

  const isLoading = isTableDisabled || isFetching || isUninitialized;

  const resourcePlanDashboardStatistic = useMemo(() => {
    if (!roleRequestData || isLoading) return null;

    return calculateResourcePlanDashboardData(roleRequestData);
  }, [roleRequestData, isLoading]);

  return (
    <div className="resource-plan-dashboard">
      <Row gutter={[16, 16]} justify="space-between">
        <Col className="total-cards" data-testid="require-action-test">
          {/* require action card */}
          <RequestTotalCard
            label="Require Action"
            total={
              resourcePlanDashboardStatistic?.requestStatusTotals
                ?.requiredAction
            }
            isLoading={isLoading}
            color={COLORS.ACCENT_ORANGE_10}
          />

          {/* declined card */}
          <RequestTotalCard
            label="Declined by project"
            total={
              resourcePlanDashboardStatistic?.requestStatusTotals?.declinedTag
            }
            isLoading={isLoading}
          />

          {/* new comments */}
          <RequestTotalCard
            label="New Comments"
            total={
              resourcePlanDashboardStatistic?.requestStatusTotals?.newComments
            }
            isLoading={isLoading}
          />
        </Col>

        {/* request total chart */}
        <Col xxl={9} xl={21} flex="1">
          <RequestStatusStatisticsChart
            chartSettings={getResourcePlanChartSettings(
              resourcePlanDashboardStatistic?.requestStatusTotals
            )}
            isLoading={isLoading}
          />
        </Col>

        {/* fte chart */}
        <Col xxl={13} xl={24} flex="1">
          <TotalFteChart
            isScrollable
            data={resourcePlanDashboardStatistic?.totalFTE}
            isLoading={isLoading}
            isProjectEnded={isProjectEnded}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ResourcePlanDashboard;
